import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    IconButton,
    Snackbar,
    Stack,
    Typography
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import XIcon from "@mui/icons-material/X";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LinkIcon from "@mui/icons-material/Link";
import Seo from "../components/Seo";
import { BLOG_ARTICLES } from "../content/blogArticles";

const SITE_URL = "https://stockmaid.com";

const formatDate = (isoDate) => new Date(`${isoDate}T00:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
});

/**
 * Parses basic inline markdown: **bold** and [text](url)
 * Returns an array of React elements.
 */
const parseInlineMarkdown = (text) => {
    if (!text) return text;

    const parts = [];
    // Match **bold** or [link text](url)
    const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        if (match[1]) {
            parts.push(<strong key={match.index}>{match[1]}</strong>);
        } else if (match[2] && match[3]) {
            const isExternal = match[3].startsWith("http");
            if (isExternal) {
                parts.push(
                    <a
                        key={match.index}
                        href={match[3]}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "var(--primary-color)" }}
                    >
                        {match[2]}
                    </a>
                );
            } else {
                parts.push(
                    <Link key={match.index} to={match[3]} style={{ color: "var(--primary-color)" }}>
                        {match[2]}
                    </Link>
                );
            }
        }

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
};

const CALLOUT_SEVERITY = {
    info: "info",
    warning: "warning",
    tip: "success",
};

const renderBlock = (block, index) => {
    switch (block.type) {
        case "paragraph":
            return (
                <Typography variant="body1" sx={{ mb: 1.5 }} key={index}>
                    {parseInlineMarkdown(block.text)}
                </Typography>
            );

        case "youtube":
            return (
                <Box key={index} sx={{ mb: 2 }}>
                    <Box
                        sx={{
                            position: "relative",
                            paddingBottom: "56.25%",
                            height: 0,
                            overflow: "hidden",
                            borderRadius: 2,
                            "& iframe": {
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                border: 0,
                            },
                        }}
                    >
                        <iframe
                            src={`https://www.youtube-nocookie.com/embed/${block.videoId}`}
                            title={block.caption || "YouTube video"}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </Box>
                    {block.caption && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                            {block.caption}
                        </Typography>
                    )}
                </Box>
            );

        case "callout":
            return (
                <Alert
                    key={index}
                    severity={CALLOUT_SEVERITY[block.variant] || "info"}
                    sx={{ mb: 2, borderRadius: 2 }}
                >
                    <Typography variant="body2">
                        {parseInlineMarkdown(block.text)}
                    </Typography>
                </Alert>
            );

        case "image":
            return (
                <Box key={index} component="figure" sx={{ mb: 2, mx: "auto", maxWidth: { xs: "100%", md: 520 }, textAlign: "center" }}>
                    <Box
                        component="img"
                        src={block.src}
                        alt={block.alt || ""}
                        loading="lazy"
                        sx={{
                            width: "100%",
                            borderRadius: 2,
                            display: "block",
                            boxShadow: 1,
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    />
                    {block.caption && (
                        <Typography component="figcaption" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {block.caption}
                        </Typography>
                    )}
                </Box>
            );

        case "list":
            return (
                <Box component="ul" key={index} sx={{ mb: 2, pl: 3, "& li": { mb: 0.5 } }}>
                    {block.items.map((item, i) => (
                        <Typography component="li" variant="body1" key={i}>
                            {parseInlineMarkdown(item)}
                        </Typography>
                    ))}
                </Box>
            );

        default:
            return null;
    }
};

const renderSection = (section) => {
    if (section.blocks) {
        return (
            <Box key={section.heading}>
                <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    {section.heading}
                </Typography>
                {section.blocks.map((block, index) => renderBlock(block, index))}
            </Box>
        );
    }

    // Legacy paragraphs format
    return (
        <Box key={section.heading}>
            <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                {section.heading}
            </Typography>
            {section.paragraphs.map((paragraph, index) => (
                <Typography variant="body1" sx={{ mb: 1.5 }} key={`${section.heading}-${index}`}>
                    {paragraph}
                </Typography>
            ))}
        </Box>
    );
};

export const BlogArticlePage = () => {
    const { articleSlug } = useParams();
    const article = BLOG_ARTICLES[articleSlug];
    const [snackOpen, setSnackOpen] = useState(false);
    const [snackMessage, setSnackMessage] = useState("Link copied to clipboard");

    if (!article) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Seo
                    title="Article Not Found | StockMaid"
                    description="This article does not exist."
                    canonicalPath={`/blog/${articleSlug || ""}`}
                    robots="noindex,nofollow"
                />
                <Typography variant="h4" component="h1" gutterBottom>
                    Article Not Found
                </Typography>
                <Typography variant="body1">
                    The requested article is not available.
                </Typography>
            </Container>
        );
    }

    const articleUrl = `${SITE_URL}/blog/${article.slug}`;

    const shareToTwitter = () => {
        window.open(
            `https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(article.title)}`,
            "_blank",
            "noopener,noreferrer"
        );
    };

    const shareToFacebook = () => {
        window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`,
            "_blank",
            "noopener,noreferrer"
        );
    };

    const shareToLinkedIn = () => {
        window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`,
            "_blank",
            "noopener,noreferrer"
        );
    };

    const copyTextFallback = (text) => {
        try {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            textArea.setAttribute("readonly", "");
            textArea.style.position = "fixed";
            textArea.style.opacity = "0";
            textArea.style.left = "-9999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const copied = document.execCommand("copy");
            document.body.removeChild(textArea);
            return copied;
        } catch (error) {
            return false;
        }
    };

    const copyLink = async () => {
        let copied = false;

        if (window.isSecureContext && navigator?.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(articleUrl);
                copied = true;
            } catch (error) {
                copied = false;
            }
        }

        if (!copied) {
            copied = copyTextFallback(articleUrl);
        }

        setSnackMessage(copied ? "Link copied to clipboard" : "Could not copy link. Copy it from the address bar.");
        setSnackOpen(true);
    };

    return (
        <Container maxWidth="md" sx={{ py: 5 }}>
            <Seo
                title={`${article.title} | StockMaid`}
                description={article.description}
                canonicalPath={`/blog/${article.slug}`}
                ogImage={article.featuredImage}
            />

            {/* Back to blog */}
            <Button
                component={Link}
                to="/blog"
                startIcon={<ArrowBackIcon />}
                size="small"
                sx={{ mb: 3 }}
            >
                Back to blog
            </Button>

            {/* Title */}
            <Typography variant="h3" component="h1" gutterBottom sx={{ color: "var(--sm-heading-color)" }}>
                {article.title}
            </Typography>

            {/* Date + read time */}
            <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                Published {formatDate(article.publishedAt)} • Updated {formatDate(article.updatedAt)}
                {article.readTime && ` • ${article.readTime}`}
            </Typography>

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    {article.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                </Stack>
            )}

            {/* Social share */}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.primary" sx={{ mr: 0.5 }}>
                    Share
                </Typography>
                <IconButton size="small" onClick={shareToTwitter} aria-label="Share on X">
                    <XIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={shareToFacebook} aria-label="Share on Facebook">
                    <FacebookIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={shareToLinkedIn} aria-label="Share on LinkedIn">
                    <LinkedInIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={copyLink} aria-label="Copy link">
                    <LinkIcon fontSize="small" />
                </IconButton>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Intro */}
            <Typography variant="body1" sx={{ mb: 3, fontSize: "1.1rem" }}>
                {parseInlineMarkdown(article.intro)}
            </Typography>

            {/* Article body */}
            <Stack spacing={3}>
                {article.sections.map(renderSection)}
            </Stack>

            <Divider sx={{ my: 4 }} />

            {/* Related Links */}
            <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                Related Links
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 4 }} flexWrap="wrap" useFlexGap>
                {article.relatedLinks.map((link) => (
                    <Button
                        key={link.to}
                        component={Link}
                        to={link.to}
                        variant="outlined"
                        size="small"
                    >
                        {link.label}
                    </Button>
                ))}
            </Stack>

            {/* More articles */}
            <Box sx={{ mt: 2, mb: 4, p: 3, bgcolor: "action.hover", borderRadius: 2 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                    Want more GPU restock tips and pricing analysis?
                </Typography>
                <Button component={Link} to="/blog" variant="contained" size="small">
                    Browse all articles
                </Button>
            </Box>

            {/* Copy link snackbar */}
            <Snackbar
                open={snackOpen}
                autoHideDuration={2000}
                onClose={() => setSnackOpen(false)}
                message={snackMessage}
            />
        </Container>
    );
};
